import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import MapPin from "../../../icons/MapPin";
import {
  amountFormatter,
  bidInterest,
  downloadPropertyDocument,
} from "services";
import { useSelector } from "react-redux";
import { BetaForm as useBetaForm } from "@reusejs/react";

interface ContactAuthorizedOfficerCardProps {
  formTitle?: string;
  propertyData: any;
  allowRedirect?: boolean;
  listingId?: string;
  cardType: "contactOfficer" | "downLoadProperties";
  styles?: string;
  setAcknowledgementOpen: any;
  setAcknowledgementDetails: any;
  setOpen?: any;
}

const ContactAuthorizedOfficerCard: React.FC<
  ContactAuthorizedOfficerCardProps
> = ({
  formTitle,
  styles,
  propertyData,
  listingId,
  allowRedirect,
  setAcknowledgementOpen,
  setAcknowledgementDetails,
  setOpen,
  cardType,
}) => {
  const { user } = useSelector((state: any) => state.auth);
  const onboardingData = useSelector((state: any) => {
    return state.bidderAuth.onboarding;
  });
  const mobileNumber =
    user?.attributes?.find((number: any) => number?.type === "mobile_number")
      ?.value || "";
  const processedNumber = mobileNumber.startsWith("+91")
    ? mobileNumber.slice(3)
    : "";

  const form = useBetaForm({
    name: onboardingData?.name || "",
    mobile: processedNumber || "",
    email:
      user?.attributes?.find((number: any) => number?.type === "email")
        ?.value || "",
  });

  useEffect(() => {
    if (setOpen) {
      form.setField("name", onboardingData?.name || "");
      form.setField("mobile", processedNumber);
      form.setField(
        "email",
        user?.attributes?.find((number: any) => number?.type === "email")
          ?.value || ""
      );
    }
  }, [setOpen]);

  const mobileNumberRegEx: RegExp = /^[0-9]{10}$/;

  const form_constraints = {
    name: {
      presence: {
        allowEmpty: false,
      },
    },
    mobile: {
      presence: {
        allowEmpty: false,
      },
      format: {
        pattern: mobileNumberRegEx,
        message: "^Mobile number is not valid.",
      },
      length: {
        maximum: 10,
        message: "^Mobile number is too long (maximum is 10 characters)",
      },
    },
    email: {
      presence: {
        allowEmpty: false,
      },
      email: true,
    },
  };

  useEffect(() => {
    form.setValidationRules(form_constraints);
  }, []);

  const clearForm = () => {
    form.setField("name", "");
    form.setField("mobile", "");
    form.setField("email", "");
  };

  const [loading, setLoading] = useState(false);

  const handleClick = () => {
    if (allowRedirect && listingId)
      window.open(`/auction-properties/${listingId}`, "_blank");
  };

  const handleSubmit = async () => {
    if (form.validate()) {
      form.value.email = form.value.email?.trim()?.toLowerCase();
      if (cardType === "contactOfficer") {
        setLoading(true);
        setAcknowledgementDetails({
          name: form.getField("name"),
          mobile: form.getField("mobile"),
          email: form.getField("email"),
          isFooter: false,
        });
        let payload: any = {
          property_id: propertyData.listing_id ? propertyData.listing_id : "",
        };
        let mobile = form.getField("mobile");
        if (mobile.match(+91)) {
          payload["mobile_number"] = mobile;
        } else {
          payload["mobile_number"] = `+91${mobile}`;
        }
        payload["email"] = form.getField("email");
        payload["name"] = form.getField("name");
        try {
          await bidInterest(payload).then((res) => {
            clearForm();
          });
        } catch (error) {
          console.log("error: ", error);
        }

        setOpen(false);
        setAcknowledgementOpen(true);
        setLoading(false);
      } else if (cardType === "downLoadProperties") {
        // handleWhatsappClick();
        setLoading(true);
        setAcknowledgementDetails({
          name: form.getField("name"),
          mobile: form.getField("mobile"),
          email: form.getField("email"),
          isFooter: true,
        });
        let payload: any = {
          property_uuid: propertyData.uuid ? propertyData.uuid : "",
        };
        let mobile = form.getField("mobile");
        if (mobile.match(+91)) {
          payload["mobile"] = mobile;
        } else {
          payload["mobile"] = `+91${mobile}`;
        }
        payload["email"] = form.getField("email");
        payload["name"] = form.getField("name");
        try {
          await downloadPropertyDocument(payload);
        } catch (error) {
          console.log("error: ", error);
        }
        clearForm();
        setOpen(false);
        setAcknowledgementOpen(true);
        setLoading(false);
      }
    }
  };

  return (
    <div className={` py-2 ${styles || ""}`}>
      {/* Property Image and Info Section */}
      <div onClick={handleClick} className="flex w-full hover:cursor-pointer">
        <img
          src={`${
            propertyData?.dump?.property_image
              ? `${process.env.NEXT_PUBLIC_IMAGIX_URL}/${propertyData?.dump?.property_image}`
              : propertyData?.dump?.asset?.placeholder_image
              ? `${process.env.NEXT_PUBLIC_IMAGIX_URL}/${propertyData.dump.asset.placeholder_image}`
              : propertyData?.images?.length > 0
              ? `${process.env.NEXT_PUBLIC_IMAGIX_URL}/${propertyData.images[0].uuid}`
              : propertyData?.sub_asset_type_details?.images?.[0]?.uuid
              ? `${process.env.NEXT_PUBLIC_IMAGIX_URL}/${propertyData?.sub_asset_type_details?.images?.[0].uuid}`
              : "/newPropertyImage.png"
          }`}
          alt="Property"
          className="w-[128px] min-h-[120px] object-cover"
        />

        <div className="bg-[#FAF7FA] clip-path-webClipPolygonTopRight py-5 px-3 flex flex-col w-full">
          <div>
            <h2 className="font-openSans text-brand-textBlack capitalize">
              {propertyData?.asset?.name} {propertyData?.sub_asset_type?.name}
            </h2>
            <div className="flex items-center mt-1">
              <MapPin />
              <span className="ml-2 text-xs font-openSans text-brand-textBlack capitalize">
                {`${propertyData?.city?.name}, ${propertyData?.state?.name}`}
              </span>
            </div>
          </div>
          <div className="flex mt-4 gap-1 items-end">
            <div className="text-xl font-semibold text-brand-textBlack whitespace-nowrap">
              {propertyData.property_date_type === "undated"
                ? "Contact us"
                : `₹${amountFormatter(propertyData.reserve_price)}`}
            </div>
            <p className="text-[10px] uppercase text-brand-headingBlue font-semibold whitespace-nowrap">
              Reserve Price
            </p>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="mt-6">
        <h3 className="font-openSans font-semibold text-[20px] leading-8 text-black">
          {formTitle || "Contact Authorized Officer"}
        </h3>
        <form
          className="flex flex-col space-y-6 mt-4"
          onSubmit={(e) => e.preventDefault()}
        >
          <FormField
            label="Name"
            type="text"
            value={form.getField("name")}
            form={form}
            formKey="name"
            placeholder="Enter name"
            onChange={(e) => form.setField("name", e.target.value)}
          />
          <FormField
            label="Mobile Number"
            type="number"
            value={form.getField("mobile")}
            form={form}
            formKey="mobile"
            placeholder="Enter number"
            onChange={(e) => form.setField("mobile", e.target.value)}
          />
          <FormField
            label="Email"
            type="email"
            value={form.getField("email")}
            form={form}
            formKey="email"
            placeholder="Enter email"
            onChange={(e) => form.setField("email", e.target.value)}
          />
          <Button
            className="mt-6 w-full"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Please wait" : "Next"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ContactAuthorizedOfficerCard;

// Form Field Component
interface FormFieldProps {
  label: string;
  type: string;
  value: string;
  placeholder: string;
  form: any;
  formKey: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const FormField: React.FC<FormFieldProps> = ({
  label,
  type,
  value,
  placeholder,
  form,
  formKey,
  onChange,
}) => (
  <div>
    <label className="font-openSans font-semibold text-web-body-sm leading-4 text-brand-bgBlack">
      {label.toUpperCase()}
    </label>
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={onChange}
      className="border-none h-10 mt-2 p-2 w-full font-openSans text-web-body-sm leading-4 text-[#A1A0A0] bg-[#F6F6F6] rounded-md focus:outline-none focus:ring-2 focus:ring-brand-bgBlack"
    />
    {form.errors.get(formKey) ? (
      <p className="mt-2 text-sm text-red-500">
        {form.errors.get(formKey)[0] === "AttributeNotRegistered"
          ? "User not registered"
          : form.errors.get(formKey)[0]}
      </p>
    ) : null}
  </div>
);
